import i18next, { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { Badge, complianceVerdictVariant } from "./badge";

type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];

interface VerdictBadgeProps {
  verdict: ComplianceVerdict;
  /**
   * The device's `device_category` (`testable_assembly` / `air_gap` /
   * `non_testable_device`). Drives the category-aware `unverified` copy —
   * omit on surfaces with no device context (falls back to the generic
   * `verdict.unverified` label).
   */
  deviceCategory?: string;
}

// Fallback translator for callers with no live React i18n context (e.g. a
// direct `verdictLabel` call outside a component). Delegates to the app's
// i18next singleton — `initI18n()` loads every namespace, including
// `compliance`, before any UI renders. `.bind` keeps the detached reference's
// `this` intact (the underlying `t` method reads `this.translator`).
const fallbackT = i18next.t.bind(i18next) as TFunction;

/**
 * Resolves the display label for a `ComplianceVerdict`. The `unverified`
 * verdict (no passing test on record) is category-aware: testable assemblies
 * read "No test on record"; air-gap and non-testable devices — which have no
 * pass/fail test, only a visual inspection — read "Inspection pending". Every
 * other verdict resolves the shared `verdict.*` label unchanged. Falls back
 * to the generic `verdict.unverified` label when `deviceCategory` is omitted
 * or unrecognized. Pass the caller's own `t` (e.g. from
 * `useTranslation("compliance")`) to keep the result reactive to a live
 * language switch; omitted, it reads the app's i18next singleton directly.
 */
export function verdictLabel(
  verdict: ComplianceVerdict,
  deviceCategory?: string,
  t: TFunction = fallbackT,
): string {
  if (verdict === "unverified" && deviceCategory) {
    return t(`verdictCategory.${deviceCategory}`, {
      ns: "compliance",
      defaultValue: t("verdict.unverified", { ns: "compliance" }),
    });
  }
  return t(`verdict.${verdict}`, { ns: "compliance" });
}

/**
 * The single per-row verdict Badge. Reuses `complianceVerdictVariant` (the
 * ONE color mapping) + `verdictLabel` (the ONE label resolver — category-
 * aware for `unverified`), so every surface (installed-devices list, facility
 * devices tab, account reverse-lookup tables) renders an identical
 * badge. `compliant` renders a green badge; `unverified` and `out_of_service`
 * render amber; the remaining verdicts render danger.
 */
export function VerdictBadge({ verdict, deviceCategory }: VerdictBadgeProps) {
  const { t } = useTranslation("compliance");
  return (
    <Badge variant={complianceVerdictVariant(verdict)}>
      {verdictLabel(verdict, deviceCategory, t)}
    </Badge>
  );
}

interface NeedsAttentionFlagProps {
  /** Active non-compliant count for the entity. 0 (or undefined) falls through to the pending arm. */
  count: number | undefined;
  /**
   * Active devices whose submitted test is still queued for review. Read only
   * when `count` is zero — a device awaiting review is not yet a violation, so
   * it never outranks a confirmed non-compliance.
   */
  pendingCount?: number;
  /** Optional click handler — when provided the flag is a button (drill-down). */
  onClick?: () => void;
}

/**
 * The single per-entity needs-attention flag, used on facility / account /
 * device-inventory list + detail surfaces. A positive count renders a danger
 * Badge "{n} out of compliance"; no non-compliance but a positive
 * `pendingCount` renders an amber "{n} awaiting review" — honest amber, not a
 * confirmed failure; neither renders a muted "OK" pill (consistency — never a
 * blank cell). When `onClick` is supplied the badge is wrapped in a button so
 * the operator can drill into the affected devices.
 */
export function NeedsAttentionFlag({ count, pendingCount, onClick }: NeedsAttentionFlagProps) {
  const { t } = useTranslation("compliance");
  const n = count ?? 0;
  const pending = pendingCount ?? 0;

  if (n <= 0 && pending <= 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        {t("needsAttention.noneLabel")}
      </span>
    );
  }

  const flagged = n > 0;
  const shown = flagged ? n : pending;
  const badge = (
    <Badge variant={flagged ? "danger" : "warning"}>
      {t(flagged ? "needsAttention.flagCount" : "needsAttention.pendingCount", { count: shown })}
    </Badge>
  );
  if (!onClick) return badge;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="cursor-pointer"
      aria-label={t(flagged ? "needsAttention.flagAria" : "needsAttention.pendingAria", {
        count: shown,
      })}
    >
      {badge}
    </button>
  );
}
